import React, { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Code, Terminal, Key, Send, CheckCircle2, Copy, Check, ArrowLeft, AlertCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { Language } from '@/types';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import RasterGridBackdrop from '@/components/ui/RasterGridBackdrop';

interface DeveloperPageProps {
  lang: Language;
}

const PYTHON_CODE = `import vexl

# Initialize the client with your preview API key
client = vexl.Client(api_key="vx_prod_...")

# Define a bounding box for spatial analysis (New Cairo area)
new_cairo_bbox = vexl.geometry.Polygon([
    [31.22, 30.01], [31.54, 30.01], 
    [31.54, 30.28], [31.22, 30.28]
])

# Run multi-spectral urban sprawl assessment
sprawl_metrics = client.analysis.urban_sprawl(
    geometry=new_cairo_bbox,
    start_date="2020-01-01",
    end_date="2026-05-01"
)

print(f"Detected Sprawl Growth: {sprawl_metrics.growth_ratio * 100:.2f}%")`;

const JS_CODE = `import { VexlClient } from '@vexl/sdk';

// Instantiate the spatial agent client
const client = new VexlClient({ apiKey: 'vx_prod_...' });

// Execute a spatial multi-modal query
const results = await client.spatial.query({
  prompt: "Identify areas prone to flash flooding in Alexandria governorate",
  confidenceThreshold: 0.85
});

console.log(\`Identified \${results.features.length} high-risk flood zones.\`);`;

export const DeveloperPage: React.FC<DeveloperPageProps> = ({ lang }) => {
  const navigate = useNavigate();
  const isAr = lang === 'ar';
  const formRef = useRef<HTMLFormElement>(null);
  const [activeTab, setActiveTab] = useState<'python' | 'js'>('python');
  const [copied, setCopied] = useState(false);
  
  // Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [useCase, setUseCase] = useState('');
  const [queries, setQueries] = useState('10k-50k');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const t = {
    heroBadge: isAr ? 'منصة المطورين // معاينة خاصة' : 'DEVELOPER PORTAL // PRIVATE PREVIEW',
    heroTitle: isAr ? 'ابنِ حلولك المكانية بذكاء خارق.' : 'Integrate spatial intelligence everywhere.',
    heroSubtitle: isAr 
      ? 'استخدم واجهات VEXL البرمجية للوصول المباشر إلى شبكة الوكلاء الجغرافيين، والاستعلام باللغة الطبيعية عن أضخم مصادر البيانات الجغرافية.'
      : 'Connect to VEXL GeoAI agent networks, execute spatial reasoning tasks, and parse multi-spectral satellite structures with a few lines of code.',
    sdkTitle: isAr ? 'مثال على استخدام المكتبات' : 'SDK Implementation Example',
    copyBtn: isAr ? 'نسخ الكود' : 'Copy Code',
    copiedBtn: isAr ? 'تم النسخ!' : 'Copied!',
    formTitle: isAr ? 'طلب وصول للمعاينة الخاصة' : 'Apply for private preview access',
    formSubtitle: isAr
      ? 'هذه الواجهة في مرحلة معاينة خاصة. أرسل حالة الاستخدام الخاصة بك ليصل الطلب مباشرة إلى فريق VEXL.'
      : 'The API is in private preview. Submit your use case and the request will be sent directly to the VEXL team.',
    formName: isAr ? 'الاسم بالكامل' : 'Full Name',
    formEmail: isAr ? 'البريد الإلكتروني المهني' : 'Work Email',
    formCompany: isAr ? 'الشركة أو المؤسسة البحثية' : 'Company or Institution',
    formUseCase: isAr ? 'اشرح باختصار سيناريو الاستخدام الخاص بك' : 'Tell us about your GeoAI use-case',
    formQueries: isAr ? 'حجم الاستعلامات الشهري المتوقع' : 'Estimated Monthly Queries',
    formSubmit: isAr ? 'إرسال طلب الانضمام' : 'Request Access Key',
    formSubmitting: isAr ? 'جاري إرسال الطلب...' : 'Submitting request...',
    successTitle: isAr ? 'تم تسجيل طلبك بنجاح!' : 'Application Registered!',
    successDesc: isAr
      ? 'شكراً لك. سيقوم فريق هندسة النظم لدينا بمراجعة طلبك وإرسال التوجيهات البرمجية ومفتاح الوصول عبر البريد الإلكتروني.'
      : 'Thank you. Our systems architecture team will review your application and email your credentials and onboarding guide.',
    errorDesc: isAr
      ? 'يرجى المحاولة مرة أخرى أو إرسال الطلب مباشرة إلى vexl.gis@gmail.com.'
      : 'Please try again or send the request directly to vexl.gis@gmail.com.',
    queriesOptions: [
      { value: 'under-10k', label: isAr ? 'أقل من 10,000 استعلام' : 'Less than 10k queries' },
      { value: '10k-50k', label: isAr ? '10,000 - 50,000 استعلام' : '10k to 50k queries' },
      { value: '50k-500k', label: isAr ? '50,000 - 500,000 استعلام' : '50k to 500k queries' },
      { value: 'over-500k', label: isAr ? 'أكثر من 500,000 استعلام' : 'Over 500k queries (Enterprise)' }
    ]
  };

  const handleCopy = () => {
    const code = activeTab === 'python' ? PYTHON_CODE : JS_CODE;
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formRef.current) return;

    const env = import.meta.env;
    if (!env.VITE_EMAILJS_SERVICE_ID || !env.VITE_EMAILJS_TEMPLATE_ID || !env.VITE_EMAILJS_PUBLIC_KEY) {
      setStatus('error');
      return;
    }

    setStatus('submitting');
    emailjs.sendForm(
      env.VITE_EMAILJS_SERVICE_ID,
      env.VITE_EMAILJS_TEMPLATE_ID,
      formRef.current,
      env.VITE_EMAILJS_PUBLIC_KEY
    )
      .then(() => {
        setStatus('success');
        setName('');
        setEmail('');
        setCompany('');
        setUseCase('');
        setQueries('10k-50k');
      })
      .catch(() => {
        setStatus('error');
      });
  };

  return (
    <PageTransition>
      <SEO
        title={isAr ? 'بوابة مطوري VEXL | معاينة GeoAI خاصة' : 'VEXL Developer Portal | Private GeoAI Preview'}
        description={isAr
          ? 'اطلب وصول المعاينة الخاصة لواجهات VEXL البرمجية وأنظمة الذكاء المكاني المتخصصة في GIS والاستشعار عن بعد.'
          : 'Apply for private preview access to VEXL APIs and spatial intelligence systems for GIS and remote sensing workflows.'}
        lang={lang}
      />
      <div 
        className={`min-h-screen bg-[#05070A] text-[#EDF0F1] pt-24 pb-24 px-6 ${isAr ? 'font-arabic' : 'font-sans'} relative overflow-hidden`}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        {/* ─── HERO SECTION (Editorial Split) ─── */}
        <section className="max-w-[1400px] mx-auto mb-16 border-b border-[#262C31] pb-16">
          <div
            className={`
              w-full grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-start
              ${isAr ? 'text-right' : 'text-left'}
            `}
          >
            {/* Left Column (Headline) */}
            <div className="lg:col-span-7">
              <h1
                className="text-3xl md:text-5xl lg:text-[54px] font-medium text-[#EDF0F1] tracking-[-0.5px] max-w-2xl leading-[1.12]"
                style={{ fontFamily: "'IBM Plex Sans Condensed', 'Inter', system-ui, sans-serif" }}
              >
                {t.heroTitle}
              </h1>
            </div>

            {/* Right Column (Sub-Description & Metadata) */}
            <div className="lg:col-span-5 space-y-4">
              <p className="text-[#9AA5AC] text-base md:text-[17px] leading-[1.6] max-w-lg font-normal">
                {t.heroSubtitle}
              </p>
              <div className="pt-1">
                <span className="inline-flex items-center gap-2 font-mono text-[11px] text-[#0E93A3] uppercase tracking-[0.18em]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#0E93A3] animate-pulse" />
                  {t.heroBadge}
                </span>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          {/* ─── LEFT: INTERACTIVE CODE BOX ─── */}
          <div className="lg:col-span-7 space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl md:text-2xl font-bold tracking-tight flex items-center gap-3 text-[#EDF0F1]">
                <Code className="text-[#0E93A3]" size={24} />
                {t.sdkTitle}
                <span className="hidden md:inline-flex rounded border border-[#0E93A3]/30 bg-[#0E93A3]/10 px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-wider text-[#0E93A3] font-semibold">
                  {isAr ? 'شكل API تجريبي' : 'Preview API shape'}
                </span>
              </h2>
              
              {/* Tab Toggles */}
              <div className="flex items-center bg-slate-900 border border-white/10 rounded-lg p-1 text-xs font-mono">
                <button
                  onClick={() => { setActiveTab('python'); setCopied(false); }}
                  className={`px-3 py-1.5 rounded-md transition-colors ${activeTab === 'python' ? 'bg-white text-black font-bold' : 'text-slate-400 hover:text-white'}`}
                >
                  Python
                </button>
                <button
                  onClick={() => { setActiveTab('js'); setCopied(false); }}
                  className={`px-3 py-1.5 rounded-md transition-colors ${activeTab === 'js' ? 'bg-white text-black font-bold' : 'text-slate-400 hover:text-white'}`}
                >
                  JavaScript
                </button>
              </div>
            </div>

            {/* Faux Code Terminal */}
            <div className="w-full bg-[#030712] border border-white/[0.08] rounded-xl overflow-hidden shadow-2xl relative">
              {/* Header bar */}
              <div className="flex items-center justify-between px-6 py-4 bg-slate-950/80 border-b border-white/[0.05]">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-500/30" />
                  <div className="w-3 h-3 rounded-full bg-yellow-500/30" />
                  <div className="w-3 h-3 rounded-full bg-green-500/30" />
                </div>
                
                <button
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-cyan-400 transition-colors font-mono"
                >
                  {copied ? (
                    <>
                      <Check size={12} className="text-emerald-400" />
                      <span className="text-emerald-400">{t.copiedBtn}</span>
                    </>
                  ) : (
                    <>
                      <Copy size={12} />
                      <span>{t.copyBtn}</span>
                    </>
                  )}
                </button>
              </div>

              {/* Code Pre Container */}
              <div className="p-6 overflow-x-auto font-mono text-xs md:text-sm leading-relaxed text-slate-300">
                <pre>{activeTab === 'python' ? PYTHON_CODE : JS_CODE}</pre>
              </div>
            </div>
          </div>

          {/* ─── RIGHT: APPLICATION FORM ─── */}
          <div className="lg:col-span-5">
            <div className="bg-slate-900/30 backdrop-blur-xl border border-white/[0.06] rounded-2xl p-8 md:p-10 shadow-2xl relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
              
              {status !== 'success' ? (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg md:text-xl font-bold mb-2 flex items-center gap-2">
                      <Key className="text-cyan-400" size={18} />
                      {t.formTitle}
                    </h3>
                    <p className="text-xs text-slate-400 leading-relaxed font-medium">
                      {t.formSubtitle}
                    </p>
                  </div>

                  <form ref={formRef} onSubmit={handleSubmit} className="space-y-5">
                    <input type="hidden" name="request_type" value="VEXL Developer Private Preview" />
                    <input type="hidden" name="expected_queries" value={queries} />
                    {/* Name */}
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t.formName}</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full bg-slate-950 border border-white/10 rounded-lg py-2.5 px-3.5 text-white text-sm focus:outline-none focus:border-cyan-500 transition-colors"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t.formEmail}</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-slate-950 border border-white/10 rounded-lg py-2.5 px-3.5 text-white text-sm focus:outline-none focus:border-cyan-500 transition-colors"
                      />
                    </div>

                    {/* Company */}
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t.formCompany}</label>
                      <input
                        type="text"
                        name="company"
                        required
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        className="w-full bg-slate-950 border border-white/10 rounded-lg py-2.5 px-3.5 text-white text-sm focus:outline-none focus:border-cyan-500 transition-colors"
                      />
                    </div>

                    {/* Queries Range */}
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t.formQueries}</label>
                      <select
                        value={queries}
                        onChange={(e) => setQueries(e.target.value)}
                        className="w-full bg-slate-950 border border-white/10 rounded-lg py-2.5 px-3.5 text-white text-sm focus:outline-none focus:border-cyan-500 transition-colors cursor-pointer"
                      >
                        {t.queriesOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    {/* Use Case */}
                    <div className="space-y-1.5">
                      <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{t.formUseCase}</label>
                      <textarea
                        name="message"
                        rows={3}
                        required
                        value={useCase}
                        onChange={(e) => setUseCase(e.target.value)}
                        className="w-full bg-slate-950 border border-white/10 rounded-lg py-2.5 px-3.5 text-white text-sm focus:outline-none focus:border-cyan-500 transition-colors resize-none"
                      />
                    </div>

                    {/* Submit Button */}
                      <button
                        type="submit"
                        disabled={status === 'submitting'}
                        className="w-full py-3 bg-white text-black font-bold text-xs tracking-wider uppercase rounded-lg hover:bg-cyan-50 active:scale-98 transition-all flex items-center justify-center gap-2 disabled:opacity-50 mt-2 shadow-lg"
                      >
                        {status === 'submitting' ? (
                        <span>{t.formSubmitting}</span>
                      ) : (
                        <>
                          <span>{t.formSubmit}</span>
                          <Send size={12} className={isAr ? 'rotate-180' : ''} />
                        </>
                      )}
                    </button>

                    {status === 'error' && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex items-start gap-2 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-3 text-xs text-red-200"
                      >
                        <AlertCircle size={15} className="mt-0.5 shrink-0 text-red-300" />
                        <span>{t.errorDesc}</span>
                      </motion.div>
                    )}
                  </form>
                </div>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center py-10 text-center"
                >
                  <div className="w-14 h-14 bg-emerald-500/10 rounded-full flex items-center justify-center border border-emerald-500/30 mb-6">
                    <CheckCircle2 size={28} className="text-emerald-400" />
                  </div>
                  <h3 className="text-xl font-black mb-3 text-white">{t.successTitle}</h3>
                  <p className="text-slate-400 text-xs md:text-sm leading-relaxed mb-8 max-w-xs">
                    {t.successDesc}
                  </p>
                  <button
                    onClick={() => setStatus('idle')}
                    className="px-8 py-2.5 bg-white text-black font-bold text-xs tracking-wider uppercase rounded-lg hover:bg-cyan-50 transition-colors"
                  >
                    {isAr ? 'حسناً' : 'Acknowledge'}
                  </button>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageTransition>
  );
};

export default DeveloperPage;
